import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import CryptoJS from 'crypto-js';
import * as Sentry from '@sentry/react';
import { setAnalyticsUser } from '@/analytics';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '@/shared/constants/api';
import HttpService, { setupAxiosInterceptors } from '@/shared/services/http.service';
import AuthService from '@/shared/services/auth.service';

interface User {
  email: string;
  name: string;
  initials: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  token: string | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SECRET_KEY = import.meta.env.VITE_AUTH_SECRET_KEY;

const encryptData = (data: string): string => {
  return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
};

const decryptData = (encryptedData: string): string => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
};

const getUserInitials = (name: string): string => {
  return name.trim().substring(0, 1).toUpperCase();
};

const fetchProfile = async (): Promise<{ email: string; username: string; role: string } | null> => {
  try {
    return await HttpService.get(API_CONFIG.path.profile, {}, { skipNotify: true });
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem('warehouse-user');
      const token = AuthService.getAccessToken();
      if (storedUser && token) {
        try {
          const decryptedUser = decryptData(storedUser);
          const parsedUser = JSON.parse(decryptedUser);
          setUser(parsedUser);
          setToken(token);
          setIsAuthenticated(true);

          const profile = await fetchProfile();
          if (profile) {
            const refreshedUser: User = {
              email: profile.email,
              name: profile.username,
              initials: getUserInitials(profile.username),
              role: profile.role,
            };
            const encryptedUser = encryptData(JSON.stringify(refreshedUser));
            localStorage.setItem('warehouse-user', encryptedUser);
            setUser(refreshedUser);
          } else {
            console.warn('Unable to refresh user profile. Logging out.');
            logout();
          }
        } catch (error) {
          console.error('Error decrypting user data:', error);
          localStorage.removeItem('warehouse-user');
          AuthService.removeAuthData();
        }
      }
      setIsLoading(false);
    };

    // Setup axios interceptors for 401 handling
    setupAxiosInterceptors(logout);

    // Small delay to ensure AuthService is ready
    const timer = setTimeout(initializeAuth, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        email: user.email,
        username: user.name,
      });
      setAnalyticsUser({ email: user.email, name: user.name });
    } else {
      Sentry.setUser(null);
      setAnalyticsUser(null);
    }
  }, [user]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await HttpService.post(API_CONFIG.path.login, {
        email,
        password,
      });

      if (response.accessToken) {
        AuthService.setAuthData(response.accessToken);

        const profile = await fetchProfile();
        if (!profile) {
          AuthService.removeAuthData();
          throw new Error(t('invalid_response_from_server'));
        }

        const user: User = {
          email: profile.email,
          name: profile.username,
          initials: getUserInitials(profile.username),
          role: profile.role,
        };

        // Store user data using AuthService
        const encryptedUser = encryptData(JSON.stringify(user));
        localStorage.setItem('warehouse-user', encryptedUser);
        AuthService.setUserData({ email: user.email, name: user.name, role: user.role });

        setUser(user);
        setToken(response.accessToken);
        setIsAuthenticated(true);
      } else {
        throw new Error(t('invalid_response_from_server'));
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(error instanceof Error ? error.message : t('login_failed'));
    }
  };

  const logout = () => {
    localStorage.removeItem('warehouse-user');
    AuthService.removeAuthData();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, token, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
